import Decimal from 'decimal.js';
import { currencyDecimals } from '@roovers/shared';

Decimal.set({ rounding: Decimal.ROUND_HALF_UP });

export type DiscountType = 'none' | 'percent' | 'amount';

export interface CalcLineInput {
  kind: 'item' | 'section';
  quantity: Decimal.Value;
  unitPrice: Decimal.Value;
  discountType: DiscountType;
  discountValue: Decimal.Value;
  /** VAT rate as a percentage, e.g. 18. */
  vatRate: Decimal.Value;
  isOptional?: boolean;
  isSelected?: boolean;
}

export interface CalcLine {
  /** False for section headings and optional lines the client has not selected. */
  counted: boolean;
  lineDiscount: Decimal;
  lineNet: Decimal;
  lineTax: Decimal;
  lineTotal: Decimal;
  baseNet: Decimal;
  baseTax: Decimal;
}

export interface CalcResult {
  lines: CalcLine[];
  subtotal: Decimal;
  discountTotal: Decimal;
  taxTotal: Decimal;
  total: Decimal;
  baseSubtotal: Decimal;
  baseTax: Decimal;
  baseTotal: Decimal;
}

const D = (v: Decimal.Value) => new Decimal(v);
const round = (v: Decimal, dp: number) => v.toDecimalPlaces(dp, Decimal.ROUND_HALF_UP);

export const BASE_CURRENCY = 'UGX';

/**
 * Money maths for every sales document (PLAN.md section 8, rule 2): rounding is applied PER LINE and
 * the lines are then summed, so a document total always equals the sum of what is printed on it.
 *   gross = qty x price, discount = % or fixed amount (never more than gross),
 *   net = gross - discount, tax = net x rate, total = net + tax.
 * Base-currency amounts are converted per line at the document exchange rate, then summed.
 */
export function computeTotals(
  lines: CalcLineInput[],
  opts: { currency: string; exchangeRate: Decimal.Value; baseCurrency?: string },
): CalcResult {
  const dp = currencyDecimals(opts.currency);
  const baseDp = currencyDecimals(opts.baseCurrency ?? BASE_CURRENCY);
  const rate = opts.currency === (opts.baseCurrency ?? BASE_CURRENCY) ? D(1) : D(opts.exchangeRate);

  const out: CalcLine[] = lines.map((l) => {
    const counted = l.kind === 'item' && !(l.isOptional && l.isSelected === false);
    if (!counted) {
      const z = D(0);
      return {
        counted: false,
        lineDiscount: z,
        lineNet: z,
        lineTax: z,
        lineTotal: z,
        baseNet: z,
        baseTax: z,
      };
    }
    const gross = round(D(l.quantity).times(l.unitPrice), dp);
    let discount = D(0);
    if (l.discountType === 'percent') discount = round(gross.times(l.discountValue).div(100), dp);
    else if (l.discountType === 'amount') discount = round(D(l.discountValue), dp);
    if (discount.gt(gross)) discount = gross;
    const net = gross.minus(discount);
    const tax = round(net.times(l.vatRate).div(100), dp);
    return {
      counted: true,
      lineDiscount: discount,
      lineNet: net,
      lineTax: tax,
      lineTotal: net.plus(tax),
      baseNet: round(net.times(rate), baseDp),
      baseTax: round(tax.times(rate), baseDp),
    };
  });

  const sum = (pick: (l: CalcLine) => Decimal) => out.reduce((s, l) => s.plus(pick(l)), D(0));
  const subtotal = sum((l) => l.lineNet);
  const taxTotal = sum((l) => l.lineTax);
  const baseSubtotal = sum((l) => l.baseNet);
  const baseTax = sum((l) => l.baseTax);
  return {
    lines: out,
    subtotal,
    discountTotal: sum((l) => l.lineDiscount),
    taxTotal,
    total: subtotal.plus(taxTotal),
    baseSubtotal,
    baseTax,
    baseTotal: baseSubtotal.plus(baseTax),
  };
}

/** Amount still owed on an invoice, in the invoice currency. */
export const outstanding = (d: {
  total: Decimal.Value;
  amountPaid: Decimal.Value;
  amountCredited: Decimal.Value;
}) => Decimal.max(D(0), D(d.total).minus(d.amountPaid).minus(d.amountCredited));

/** Whole days between two dates (a minus b), ignoring the time of day. */
export const daysBetween = (a: Date, b: Date) =>
  Math.floor(
    (Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate()) -
      Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate())) /
      86_400_000,
  );

export type AgeBucket = 'current' | 'd1_30' | 'd31_60' | 'd61_90' | 'd90_plus';

/** Receivables ageing bucket by days past the due date. Not yet due = current. */
export function ageBucket(dueDate: Date | null, asOf: Date): AgeBucket {
  if (!dueDate) return 'current';
  const late = daysBetween(asOf, dueDate);
  if (late <= 0) return 'current';
  if (late <= 30) return 'd1_30';
  if (late <= 60) return 'd31_60';
  if (late <= 90) return 'd61_90';
  return 'd90_plus';
}
